// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Divider, Grid2, IconButton, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseAutocomplete, BaseInput, BaseInputAddress, BaseLabel, BaseSelect, Loading } from '~/components/common';
// Others
import { IFormOrder } from '~/utils/interface/order';
import { IBaseResponse } from '~/utils/interface/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { TypeDetailClientEnum } from '~/utils/enum/common';
import { formatPhoneNumber, getFullName } from '~/utils/helper/common';
import { getDetailClient, getListClient } from '~/thunks/client/clientThunk';
import { IBaseAddress, IBaseOption, IResultCallApiFn } from '~/utils/interface/common';
import { IClientOverview, IParamDetailClient, IQueryParamClient } from '~/utils/interface/client';
import {
  EMPTY_STRING,
  optionSiteType,
  DEFAULT_MAX_PAGE,
  DEFAULT_CURRENT_PAGE,
  optionContactTypeClient,
  optionBusinessTypeClient,
} from '~/utils/constants/common';
// Styles, images, icons
import styles from './ClientInformationOrderForm.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const ClientInformationOrderForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base', 'admin/quotes']);
  const dispatch = useAppDispatch();
  const { id: orderId } = useParams();
  const [searchParams] = useSearchParams();
  const clientIdParams = searchParams.get('clientId');

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useFormContext<IFormOrder>();

  const {
    fields: additionalContactsFields,
    append: appendAdditionalContacts,
    remove: removeAdditionalContacts,
  } = useFieldArray({
    control,
    name: 'additionalContacts',
  });

  const { clientId, additionalContacts } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const isFirstLoadClientDetailRef = useRef(true);
  const [optionClient, setOptionClient] = useState<IBaseOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingClient, setIsLoadingClient] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    return () => {
      isFirstLoadClientDetailRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!clientIdParams || orderId) return;

    setValue('clientId', clientIdParams);
  }, [clientIdParams]);

  useEffect(() => {
    const paramObject = {
      page: DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_MAX_PAGE,
    };

    handleGetListClient(paramObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clientId) return;

    if (isFirstLoadClientDetailRef.current && !!orderId) {
      isFirstLoadClientDetailRef.current = false;
      return;
    }

    const params: IParamDetailClient = {
      clientId: clientId,
      type: TypeDetailClientEnum.OVERVIEW,
    };

    handleGetDetailClient(params);
  }, [clientId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListClient = (params: IQueryParamClient) => {
    setIsLoadingClient(true);

    dispatch(getListClient(params))
      .unwrap()
      .then((res) => {
        const { responses: listClient } = res?.data;

        if (!listClient) return;

        const convertOptionClient: IBaseOption[] = listClient.map((item) => ({
          value: item.id,
          label:
            item?.firstName || item?.lastName
              ? getFullName({ firstName: item?.firstName, lastName: item?.lastName })
              : item?.entityName,
        }));

        setOptionClient(convertOptionClient);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoadingClient(false);
      });
  };

  const handleGetDetailClient = (params: IParamDetailClient) => {
    setIsLoading(true);

    dispatch(getDetailClient(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IClientOverview>;

        if (!data) return;

        const { id, ...address } = data.address;

        setValue('firstName', data.firstName || EMPTY_STRING);
        setValue('lastName', data.lastName || EMPTY_STRING);
        setValue('clientTitle', data.title || EMPTY_STRING);
        setValue('companyName', data.companyName || EMPTY_STRING);
        setValue('businessType', data.businessType || undefined);
        setValue('clientAddress', address);
        setValue('paymentTerms', data.paymentTerms || EMPTY_STRING);
        setValue('deliveryAddress', address || EMPTY_STRING);
        setValue('pickupAddress', address || EMPTY_STRING);
        setValue(`clientContacts.${0}.email`, data?.clientContacts[0]?.email || EMPTY_STRING, {
          shouldValidate: true,
        });
        setValue(
          `clientContacts.${0}.phoneNumber`,
          formatPhoneNumber(data?.clientContacts[0]?.phoneNumber) || EMPTY_STRING,
          {
            shouldValidate: true,
          }
        );
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleChangeClient = (value: IBaseOption | IBaseOption[] | null) => {
    const data = value as IBaseOption;

    setValue('clientId', data.value);
  };

  const handleChangeAddress = (value: IBaseAddress) => {
    reset((prev) => ({
      ...prev,
      clientAddress: value,
    }));
  };

  const handleGetOptionContactTypeClient = (index: number) => {
    const selectedRelationships = additionalContacts?.map((c) => c.contactType) || [];

    const selected = selectedRelationships.filter((_, i) => i !== index);

    return optionContactTypeClient.filter((opt) => !selected.includes(opt.value));
  };

  const handleAddAdditionalContact = () => {
    appendAdditionalContacts({
      // countryCode: EMPTY_STRING,
      email: EMPTY_STRING,
      firstName: EMPTY_STRING,
      phoneNumber: EMPTY_STRING,
      contactType: EMPTY_STRING,
      businessType: EMPTY_STRING,
    });
  };

  const fetchOptionsSiteType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionSiteType,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsType = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionBusinessTypeClient,
      hasMoreResp: false,
    };

    return result;
  }, []);
  //#endregion Handle Function

  return (
    <Box id='clientInformationOrderFormComponent' className={cx('container')}>
      <BaseLabel styleType='title' label={t('form_orders_title_client_information')} icon={icons.commonIconClient} />
      <Controller
        control={control}
        name={'clientId'}
        render={({ field: { value, onChange } }) => {
          return (
            <BaseAutocomplete
              width={'42%'}
              options={optionClient}
              textSearch={optionClient.find((item) => item.value === value)?.label || EMPTY_STRING}
              onTextSearchChange={onChange}
              onChange={handleChangeClient}
              placeholder={t('form_orders_placeholder_search_client')}
              isLoading={isLoadingClient}
            />
          );
        }}
      />
      <Divider className={cx('divider')} textAlign='center'>
        {t('form_orders_divider_new_client')}
      </Divider>
      <Grid2 container direction={'row'} spacing={'14px'} marginTop={'10px'}>
        <Grid2 size={6}>
          <BaseLabel styleType='subTitle' label={t('form_orders_title_personal_information')} />

          <Grid2 container columnSpacing={'10px'} rowSpacing={'6px'} marginTop={'14px'}>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'firstName'}
                render={({ field: { onChange, value } }) => {
                  return <BaseInput label={t('form_orders_label_first_name')} value={value} onChange={onChange} />;
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'lastName'}
                render={({ field: { onChange, value } }) => {
                  return <BaseInput label={t('form_orders_label_last_name')} value={value} onChange={onChange} />;
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={`clientContacts.0.email`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_orders_label_email')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.clientContacts?.[0]?.email?.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={`clientContacts.0.phoneNumber`}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_orders_label_phone')}
                      value={value}
                      onChange={(event) => {
                        onChange(formatPhoneNumber(event.target.value));
                      }}
                      errorMessage={errors?.clientContacts?.[0]?.phoneNumber?.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'companyName'}
                render={({ field: { onChange, value } }) => {
                  return <BaseInput label={t('form_orders_label_company')} value={value} onChange={onChange} />;
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'clientTitle'}
                render={({ field: { onChange, value } }) => {
                  return <BaseInput label={t('form_orders_label_title')} value={value} onChange={onChange} />;
                }}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name={'businessType'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseSelect
                      gap={8}
                      height={36}
                      styleType='contained'
                      label={t('form_orders_label_type')}
                      callApiFn={fetchOptionsType}
                      value={value}
                      onChange={(data) => onChange(data?.value)}
                      errorMessage={errors?.businessType?.message}
                    />
                  );
                }}
              />
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={6}>
          <BaseLabel styleType='subTitle' label={t('form_quotes_title_address_information', { ns: 'admin/quotes' })} />

          <Grid2 container columnSpacing={'10px'} rowSpacing={'6px'} marginTop={'14px'}>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'clientAddress.address'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInputAddress
                      label={t('form_quotes_label_address', { ns: 'admin/quotes' })}
                      value={value}
                      onChange={onChange}
                      onSelectAddress={handleChangeAddress}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                control={control}
                name={'clientAddress.unitDetail'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_quotes_label_suite', { ns: 'admin/quotes' })}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={3}>
              <Controller
                control={control}
                name={'clientAddress.city'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_quotes_label_city', { ns: 'admin/quotes' })}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={3}>
              <Controller
                control={control}
                name={'clientAddress.state'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_quotes_label_state', { ns: 'admin/quotes' })}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={3}>
              <Controller
                control={control}
                name={'clientAddress.postalCode'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      label={t('form_quotes_label_zip', { ns: 'admin/quotes' })}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Grid2>
            {/* <Grid2 size={3}>
              <Controller
                control={control}
                name={'clientAddress.country'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseInput
                      
                      label={t('form_quotes_label_country', { ns: 'admin/quotes' })}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Grid2> */}
            <Grid2 size={3}>
              <Controller
                control={control}
                name={'clientAddress.siteType'}
                render={({ field: { onChange, value } }) => {
                  return (
                    <BaseSelect
                      gap={8}
                      styleType='contained'
                      label={t('form_quotes_label_site_type', { ns: 'admin/quotes' })}
                      callApiFn={fetchOptionsSiteType}
                      value={value}
                      onChange={(data) => onChange(data?.value)}
                    />
                  );
                }}
              />
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
      {!clientId && (
        <>
          <Grid2 size={12} container>
            <BaseLabel styleType='subTitle' label={t('form_quotes_title_contact_information')} />

            <Grid2 size={12} container columnSpacing={'10px'} rowSpacing={'6px'} marginTop={'14px'}>
              {additionalContactsFields.map((contact, index) => (
                <>
                  <Grid2 size={2.2}>
                    <Controller
                      key={contact.id ? `${contact.id}-businessType` : `${index}-businessType`}
                      control={control}
                      name={`additionalContacts.${index}.businessType`}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <BaseSelect
                            gap={8}
                            styleType='contained'
                            label={t('form_quotes_label_type')}
                            callApiFn={fetchOptionsType}
                            value={value}
                            onChange={(data) => onChange(data?.value)}
                            errorMessage={errors?.businessType?.message}
                          />
                        );
                      }}
                    />
                  </Grid2>

                  <Grid2 size={2.2}>
                    <Controller
                      key={contact.id ? `${contact.id}-firstName` : `${index}-firstName`}
                      control={control}
                      name={`additionalContacts.${index}.firstName`}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <BaseInput
                            label={t('form_quotes_label_name')}
                            value={value}
                            onChange={onChange}
                            errorMessage={errors?.additionalContacts?.[index]?.firstName?.message}
                          />
                        );
                      }}
                    />
                  </Grid2>

                  <Grid2 size={2.2}>
                    <Controller
                      key={contact.id ? `${contact.id}-email` : `${index}-email`}
                      control={control}
                      name={`additionalContacts.${index}.email`}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <BaseInput
                            label={t('form_quotes_label_email')}
                            value={value}
                            onChange={onChange}
                            errorMessage={errors?.additionalContacts?.[index]?.email?.message}
                          />
                        );
                      }}
                    />
                  </Grid2>

                  <Grid2 size={2.2}>
                    <Controller
                      key={contact.id ? `${contact.id}-phoneNumber` : `${index}-phoneNumber`}
                      control={control}
                      name={`additionalContacts.${index}.phoneNumber`}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <BaseInput
                            label={t('form_quotes_label_phone')}
                            value={value}
                            onChange={(event) => {
                              onChange(formatPhoneNumber(event.target.value));
                            }}
                            errorMessage={errors?.additionalContacts?.[index]?.phoneNumber?.message}
                          />
                        );
                      }}
                    />
                  </Grid2>

                  <Grid2 size={2.2}>
                    <Controller
                      key={contact.id ? `${contact.id}-contactType` : `${index}-contactType`}
                      control={control}
                      name={`additionalContacts.${index}.contactType`}
                      render={({ field: { onChange, value } }) => {
                        return (
                          <BaseAutocomplete
                            showIcon={false}
                            placeholder={EMPTY_STRING}
                            value={optionContactTypeClient.find((item) => item.value === value) || null}
                            options={handleGetOptionContactTypeClient(index)}
                            textSearch={
                              optionContactTypeClient.find((item) => item.value === value)?.label || EMPTY_STRING
                            }
                            onTextSearchChange={onChange}
                            onChange={(option) => {
                              const data = option as IBaseOption;

                              onChange(data?.value);
                            }}
                            label={t('form_quotes_label_relationship')}
                            errorMessage={errors?.additionalContacts?.[index]?.contactType?.message}
                          />
                        );
                      }}
                    />
                  </Grid2>

                  <Grid2 size={0.8} className={cx('boxRemove')}>
                    <IconButton
                      color='error'
                      key={contact.id ? `${contact.id}-delete` : `${index}-delete`}
                      className={cx('buttonRemove')}
                      onClick={() => removeAdditionalContacts(index)}
                    >
                      <img src={icons.commonIconTrash} alt={t('common_alt_img')} height={24} width={24} />
                    </IconButton>
                  </Grid2>
                </>
              ))}
            </Grid2>
          </Grid2>

          <Typography component={'span'} className={cx('buttonAdd')} onClick={handleAddAdditionalContact}>
            {t('common_button_add_item', { ns: 'common/base' })}
          </Typography>
        </>
      )}
      {isLoading && <Loading />}
    </Box>
  );
};

export default ClientInformationOrderForm;
